/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  SvgIcon,
  Button,
  ButtonGroup,
  Heading,
  TextArea,
  SMSSend,
  EmailSend
} from '../'

class CaseCommunicationAdd extends React.Component {
  static propTypes = {
    affectsIdentity: PropTypes.string.isRequired,
    channel: PropTypes.oneOf(['casenote', 'email', 'sms']),
    onAdd: PropTypes.func,
    className: PropTypes.string,
    disabled: PropTypes.bool
  }

  constructor(props) {
    super(props)

    this.state = {
      messageContent: '',
      communicationChannel: props.channel || "casenote",  // default
      messageSendSuccess: false,
      messageSendFail: false,
      sendEmails: false
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.identity && nextProps.data.identity.activeEmailAddresses.edges.length > 0) {
      this.setState({ sendEmails: true })
    }
  }

  getHeading = () => {
    if (this.state.communicationChannel === "casenote") {
      return "Add a case note"
    }
    if (this.state.communicationChannel === "email") {
      return "Send an Email"
    }
    if (this.state.communicationChannel === "sms") {
      return "Send an SMS"
    }
  }

  getInputForm = () => {
    if (this.state.communicationChannel === "casenote") {
      return (
        <React.Fragment>
          <TextArea
            value={this.state.messageContent}
            onChange={(e) => this.setState({ messageContent: e.target.value })}
            className="fullwidth"
            rows={3}
            disabled={this.props.disabled}
          />
          <div className="slds-m-top_medium">
            <Button
              type="brand"
              onClick={() => {
                this.props.onAdd(this.state.messageContent)
                this.setState({ messageContent: '' })
              }}
              minWidth='100%'
              disabled={this.state.messageContent.length === 0}>
              ADD NOTE
            </Button>
          </div>
        </React.Fragment>
      )
    }
    if (this.state.communicationChannel === "email") {
      return (
        <EmailSend
          identity={this.props.data.identity.id}
          onEmailCreateSuccess={(id) => {
            this.props.onAdd(id)
            this.setState({ messageSendSuccess: true })
          }}
          onEmailCreateFail={() => this.setState({ messageSendFail: true })}
        />
      )
    }
    if (this.state.communicationChannel === "sms") {
      return (
        <SMSSend
          toIdentityId={this.props.data.identity.id}
          onMessageCreateSuccess={(id) => {
            this.props.onAdd(id)
            this.setState({ messageSendSuccess: true })
          }}
          onMessageCreateFail={() => this.setState({ messageSendFail: true })}
        />
      )
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className="slds-is-relative" />
    }

    if (this.props.data.error) {
      return (
        <div className="slds-p-around_x-large">
          <SvgIcon
            variant="utility"
            symbol="warning"
            colour="error"
          />
          Failed to fetch case information.
        </div>
      )
    }

    return (
      <div className={`slds-grid slds-wrap case-timeline-input ${this.props.className ? this.props.className : ''}`}>
        <div className="slds-col">
          {!this.props.channel &&
            <ButtonGroup className="slds-m-bottom_medium">
              <Button
                onClick={() => {
                  this.setState({
                    communicationChannel: "casenote",
                    messageContent: '',
                    messageSendSuccess: false,
                    messageSendFail: false
                  })
                }}
                disabled={this.props.disabled}>
                <SvgIcon
                  variant="picsaos"
                  symbol="casenote"
                  colour={this.state.communicationChannel === "casenote" ? "success" : "default"}
                />
              </Button>
              {this.state.sendEmails
                ? <Button
                    onClick={() => {
                      this.setState({
                        communicationChannel: "email",
                        messageContent: '',
                        messageSendSuccess: false,
                        messageSendFail: false
                      })
                    }}
                    disabled={this.props.disabled}>
                    <SvgIcon
                      variant="picsaos"
                      symbol="email"
                      colour={this.state.communicationChannel === "email" ? "success" : "default"}
                    />
                  </Button>
                : <span></span>
              }
              <Button
                onClick={() => {
                  this.setState({
                    communicationChannel: "sms",
                    messageContent: '',
                    messageSendSuccess: false,
                    messageSendFail: false
                  })
                }}
                disabled={this.props.disabled}>
                <SvgIcon
                  variant="picsaos"
                  symbol="sms"
                  colour={this.state.communicationChannel === "sms" ? "success" : "default"}
                />
              </Button>
            </ButtonGroup>
          }
          <div>
            <Heading size="medium">
              {this.getHeading()}
            </Heading>
            {this.state.messageSendSuccess
              ? <div className="slds-m-top_x-large">
                  Message was successfully queued for sending. <Button type="reset" onClick={() => this.setState({ messageSendSuccess: false })}>Send another</Button>
                </div>
              : this.state.messageSendFail
                ? <div>
                    <SvgIcon
                      variant="utility"
                      symbol="warning"
                      colour="error"
                      size="small"
                      className="slds-m-left_small slds-m-right_small"
                    />
                    Message failed to queue. Please <Button type="reset" onClick={() => this.setState({ messageSendFail: false })}>retry</Button> or contact support if this continues to fail.
                  </div>
                : this.getInputForm()
            }
          </div>
        </div>
      </div>
    )
  }
}

const getCaseAffectsIdentity = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      activeEmailAddresses: addresses(addressType: "email", optedOut: false, inactive: false) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`

const withData =
  graphql(getCaseAffectsIdentity, {
    options: (ownProps) => ({
      variables: {
        id: ownProps.affectsIdentity
      }
    })
  })

export default withData(CaseCommunicationAdd)



// WEBPACK FOOTER //
// ./src/components/case-communication-add/index.js